"use strict";
// distillation of https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/live-announcer/src/LiveAnnouncer.tsx
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiveAnnouncer = exports.DefaultRemovalTimeout = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var ReactDOM = tslib_1.__importStar(require("react-dom"));
var PreventUserSelect_1 = require("../styles/PreventUserSelect");
exports.DefaultRemovalTimeout = 7000; // exported for tests
/**
 * Essentially a toaster which appears only to assistive technology (AT).
 * All messages MUST be localized.
 *
 * Primary use case is for providing status updates in response to user actions which are otherwise visual-dependent.
 * For example, clicking a button which rearranges the order of items in another component, without moving focus to that other component.
 * In this scenario, AT must be provided notification of the change, e.g. `LiveAnnouncer.announce('Item moved to position 4 from position 3.')`
 */
exports.LiveAnnouncer = {
    /**
     * @param message The message to announce to assistive technology.
     * @param assertiveness
     * `'assertive'` will interrupt any current announcement.
     * `'polite'` will wait to speak until the user is idle.
     * @param removalTimeout The number of milliseconds to wait after announcing the message, before removing it from the DOM.
     *                       Defaults to `7000` (subject to change in the future).
     */
    announce: function (message, assertiveness, removalTimeout) {
        if (assertiveness === void 0) { assertiveness = 'assertive'; }
        if (removalTimeout === void 0) { removalTimeout = exports.DefaultRemovalTimeout; }
        ensureInstance(function (announcer) { return announcer.announce(message, assertiveness, removalTimeout); });
    },
    /**
     * @param assertiveness the assertiveness queue to clear, or `undefined` for all
     */
    clear: function (assertiveness) {
        ensureInstance(function (announcer) { return announcer.clear(assertiveness); });
    },
    /** Removes the announcer from the DOM. Should be used only for cleanup during unit tests. */
    destroy: function () {
        if (AnnouncerRef.current && mountPoint) {
            ReactDOM.unmountComponentAtNode(mountPoint);
            document.body.removeChild(mountPoint);
            mountPoint = null;
        }
    }
};
var AnnouncerRef = React.createRef();
var mountPoint = null;
/** Enforce a singleton so we don't end up with competing aria-live regions. */
function ensureInstance(callback) {
    if (!AnnouncerRef.current) {
        mountPoint = document.createElement('div');
        mountPoint.dataset.liveAnnouncer = ''; // i.e. <div data-live-announcer />
        document.body.prepend(mountPoint);
        ReactDOM.render(React.createElement(LiveRegionAnnouncer, { ref: AnnouncerRef }), mountPoint, function () { return callback(AnnouncerRef.current); });
    }
    else {
        callback(AnnouncerRef.current);
    }
}
// the brave little invisible toaster.gif
var messageId = 0;
var LiveRegionAnnouncer = React.forwardRef(function (_, ref) {
    var _a = React.useState([]), assertiveMessages = _a[0], setAssertiveMessages = _a[1];
    var _b = React.useState([]), politeMessages = _b[0], setPoliteMessages = _b[1];
    React.useImperativeHandle(ref, function () { return ({
        announce: function (message, assertiveness, removalTimeout) {
            if (assertiveness === void 0) { assertiveness = 'assertive'; }
            if (!message) {
                return;
            }
            var id = messageId++;
            var setMessages = assertiveness === 'assertive' ? setAssertiveMessages : setPoliteMessages;
            setMessages(function (messages) { return tslib_1.__spreadArrays(messages, [{ id: id, text: message }]); });
            window.setTimeout(function () {
                if (AnnouncerRef.current) {
                    setMessages(function (messages) { return messages.filter(function (msg) { return msg.id !== id; }); });
                }
            }, removalTimeout);
        },
        clear: function (assertiveness) {
            if (!assertiveness || assertiveness === 'assertive') {
                setAssertiveMessages([]);
            }
            if (!assertiveness || assertiveness === 'polite') {
                setPoliteMessages([]);
            }
        }
    }); });
    return React.createElement(React.Fragment, null,
        React.createElement(MessageLog, { "aria-live": 'assertive', key: 'assertive' }, assertiveMessages.map(function (message) { return React.createElement("div", { key: message.id }, message.text); })),
        React.createElement(MessageLog, { "aria-live": 'polite', key: 'polite' }, politeMessages.map(function (message) { return React.createElement("div", { key: message.id }, message.text); })));
});
LiveRegionAnnouncer.displayName = 'LiveRegionAnnouncer';
// personally I find this technique ham-fisted --ckovatch
var VisuallyHiddenStyle = tslib_1.__assign(tslib_1.__assign({}, PreventUserSelect_1.PreventUserSelectSansCursor), { border: 0, clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', height: 1, margin: '0 -1px -1px 0', overflow: 'hidden', padding: 0, position: 'absolute', width: 1, whiteSpace: 'nowrap' });
var MessageLog = function (_a) {
    var style = _a.style, props = tslib_1.__rest(_a, ["style"]);
    return React.createElement("div", tslib_1.__assign({}, props, { "aria-relevant": 'additions', role: 'log', style: tslib_1.__assign(tslib_1.__assign({}, VisuallyHiddenStyle), style) }));
};
MessageLog.defaultProps = { 'aria-live': 'assertive' };
MessageLog.displayName = 'MessageLog';
//# sourceMappingURL=LiveAnnouncer.js.map